import { SaleDepositResponse } from "../../types/sale-deposit.type";
import { DepositReviewSide } from "./useDepositActions";

/**
 * Why a ตรวจแล้ว box is dead, in the words shown on the box itself.
 *
 * Each reason is something the reader can act on — send the slip, reopen the day, or nothing
 * at all because the row is already signed — and a grey checkbox that explains none of them
 * is indistinguishable from a bug.
 */
export const REVIEW_BLOCKED_REASON = {
  noTransfer: "ยังไม่ได้ส่งสลิป จึงยังไม่มียอดโอนให้ตรวจ",
  branchDayClosed: "ปิดศูนย์เซลของวันนั้นแล้ว จึงแก้ไขผลการตรวจสอบไม่ได้",
  saving: "กำลังบันทึกผลการตรวจสอบของรายการนี้",
  alreadyReviewed: "คุณตรวจรายการนี้แล้ว",
};

interface ReviewGateInput {
  row: SaleDepositResponse;
  /** A sign-off on this row is already in flight; its response decides what the box shows. */
  reviewSaving: boolean;
  /** Head-office accounting supervision has closed the branch-day, freezing every row in it. */
  branchDayClosed: boolean;
}

interface ReviewSelectionGateInput extends ReviewGateInput {
  /** Which of the two columns this caller's own tick fills in. */
  side: DepositReviewSide;
}

/**
 * Why this caller's ตรวจแล้ว cannot be written on the row — Thai, or null when it can.
 *
 * There is nothing to verify until a transfer has been submitted, and a closed branch-day is
 * frozen: the closing was signed on how many routes each side had ticked, so a tick moved
 * afterwards would contradict it. A correction to a closed day goes through คำขอแก้ไขยอดโอน.
 */
export const reviewBlockedReason = ({
  row,
  reviewSaving,
  branchDayClosed,
}: ReviewGateInput): string | null => {
  if (row.transfer_amount == null) return REVIEW_BLOCKED_REASON.noTransfer;
  if (branchDayClosed) return REVIEW_BLOCKED_REASON.branchDayClosed;
  if (reviewSaving) return REVIEW_BLOCKED_REASON.saving;
  return null;
};

/**
 * Which ตรวจแล้ว column a caller's own tick fills in. The server reads the same thing off the
 * token — a cross-branch role signs the HO box, a branch role the BO box — and never from the
 * request, so all this has to do is agree with it.
 */
export const callerReviewSide = (canReviewAsHo: boolean): DepositReviewSide =>
  canReviewAsHo ? "ho" : "bo";

/**
 * Why the row cannot be picked for a bulk sign-off — the box's own reasons first, then the one
 * belonging to the selection alone.
 *
 * The bulk write only ever ticks ON, so a row this caller has already signed would be sent for
 * nothing and come back counted as skipped. Built on top of the box's rules rather than beside
 * them: a row the box refuses must never be selectable, whichever rule refused it.
 */
export const selectionBlockedReason = (
  input: ReviewSelectionGateInput,
): string | null => {
  const blocked = reviewBlockedReason(input);
  if (blocked) return blocked;
  const reviewedByCaller =
    input.side === "ho" ? input.row.ho_reviewed : input.row.bo_reviewed;
  return reviewedByCaller ? REVIEW_BLOCKED_REASON.alreadyReviewed : null;
};
